import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import logo from '../assets/images/new-logo.svg';
import logoSmall from '../assets/images/new-mobile-logo.svg';
import {
  useLogin,
  useLogout,
  usePrivy,
  useWallets,
} from '@privy-io/react-auth';
import { IconAlertTriangleFilled, IconPower } from '@tabler/icons-react';
import { shortenAddress } from '../shared/utils/Formatter';
import { queryClient } from '../App';
import { useAccount } from 'wagmi';
import { useEffect, useState } from 'react';
import ProfileBanner from '../pages/ProfileBanner';
import { activeChain, checkNetwork } from '../shared/utils/CheckNetwork';
import * as chains from 'viem/chains';
import OneSignal from 'react-onesignal';
import { useLocation, useNavigate } from 'react-router-dom';

const AppBarStyle: SxProps<Theme> = {
  backgroundColor: '#fff',
  padding: '1rem',
  borderBottom: '1px solid #F2F6F7',
  position: 'fixed',
  width: '100%',
  zIndex: 1,
};

const DesktopLogoStyle: SxProps<Theme> = {
  width: '130px',
  height: 'auto',
  paddingLeft: '1rem',
  display: {
    xs: 'none',
    md: 'block',
  },
};

const MobileLogoStyle: SxProps<Theme> = {
  width: '40px',
  height: 'auto',
  paddingLeft: '0.25rem',
  display: {
    xs: 'block',
    md: 'none',
  },
};

const headerStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  color: '#000',
};

const authenticatedStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

const btnConnect: SxProps<Theme> = {
  lineHeight: 'unset',
  height: 'unset',
  minHeight: 'unset',
  padding: 0,
};

const Header = () => {
  const { ready, authenticated, user } = usePrivy();
  const { chainId } = useAccount();
  const { wallets } = useWallets();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { logout } = useLogout({
    onSuccess: () => {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('beforeLogin');
      queryClient.invalidateQueries();
      OneSignal.logout();
    },
  });

  const { login } = useLogin();

  const getChain = (chainId: number) => {
    for (const chain of Object.values(chains)) {
      if (chain.id === chainId) {
        return chain;
      }
    }
  };

  useEffect(() => {
    if (
      ready &&
      authenticated &&
      wallets.length > 0 &&
      user?.wallet &&
      user?.wallet?.address !== wallets[0].address &&
      user?.wallet?.connectorType !== 'embedded'
    ) {
      logout();
    }
  }, [authenticated, ready, wallets, user?.wallet]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <>
      <AppBar position="static" elevation={0} sx={AppBarStyle}>
        <Box sx={headerStyle}>
          <Box display="flex" justifyContent="center">
            <Box
              component="img"
              src={logo}
              alt="logo"
              sx={DesktopLogoStyle}
              onClick={() => navigate('/')}
            />
            <Box
              component="img"
              src={logoSmall}
              alt="logo-small"
              sx={MobileLogoStyle}
              onClick={() => navigate('/')}
            />
          </Box>
          <Box display="flex" justifyContent="center">
            {ready ? (
              <>
                {authenticated && user?.wallet && chainId && (
                  <>
                    <Box
                      mr={3}
                      position="relative"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      gap={1}
                      paddingY={0.5}
                      paddingX={1}
                      style={{
                        borderRadius: 5,
                        cursor: 'pointer',
                        color: chainId === activeChain.id ? '#43BDCE' : 'black',
                        border: `1px solid ${
                          chainId === activeChain.id ? '#43BDCE' : 'orange'
                        }`,
                      }}
                      onClick={() => checkNetwork()}
                    >
                      {getChain(chainId ?? 0)?.name ?? 'Unsupported Network'}
                      {chainId !== activeChain.id && (
                        <IconAlertTriangleFilled
                          size={14}
                          style={{
                            color: 'orange',
                          }}
                        />
                      )}
                    </Box>
                  </>
                )}

                {authenticated && user?.wallet ? (
                  <Box sx={authenticatedStyle}>
                    {user?.wallet?.address && (
                      <Typography
                        style={{ cursor: 'pointer' }}
                        onClick={() => setIsDrawerOpen(true)}
                      >
                        {shortenAddress(user?.wallet?.address || '')}
                      </Typography>
                    )}

                    <Button
                      sx={btnConnect}
                      onClick={async () => {
                        logout();
                      }}
                    >
                      <IconPower />
                    </Button>
                  </Box>
                ) : (
                  <Button onClick={login}>Connect</Button>
                )}
              </>
            ) : (
              <CircularProgress
                size={24}
                sx={{ padding: { sx: '0 20px', md: '3px 20px' } }}
              />
            )}
          </Box>
        </Box>
      </AppBar>
      <ProfileBanner
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
        }}
        walletAddress={user?.wallet?.address as `0x${string}`}
        onLogout={async () => {
          logout();
        }}
      />
    </>
  );
};

export default Header;
